'use client';

import Link from 'next/link';
import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import ScrollFadeIn from '@/components/ui/ScrollFadeIn';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.25, 0.4, 0.25, 1] as const },
  }),
};

function InView({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

function SectionLabel({ children, color = 'emerald' }: { children: React.ReactNode; color?: 'emerald' | 'blue' | 'gold' }) {
  return <p className={`text-xs font-medium uppercase tracking-widest mb-3 ${color === 'blue' ? 'text-accent-secondary' : 'text-accent'}`}>{children}</p>;
}

function Check({ blue = false }) {
  return (
    <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${blue ? 'text-accent-secondary' : 'text-accent'}`} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );
}

// Six specialized agents from the deck: Discover → Prioritize → Generate → Apply → Deploy → Rollback
type Agent = { id: string; label: string; color: 'emerald' | 'blue' | 'gold'; headline: string; desc: string; pills: string[]; icon: React.ReactNode };
const agents: Agent[] = [
  {
    id: 'discover', label: 'Discover Agent', color: 'emerald',
    headline: 'Full-stack asset intel',
    desc: 'Continuously scans code repos, cloud configs, container registries, endpoints, and AI agent APIs. Builds a live asset inventory across your entire environment.',
    pills: ['Code repos', 'Cloud configs', 'Containers', 'Endpoints', 'AI agent APIs'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
  },
  {
    id: 'prioritize', label: 'Prioritize Agent', color: 'emerald',
    headline: 'AI risk analysis',
    desc: 'Scores vulnerabilities using CVSS + EPSS exploitability + business context: asset criticality, exposure, and blast radius. Fix what matters most, first.',
    pills: ['CVSS scoring', 'EPSS exploitability', 'Blast radius', 'Business context'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    id: 'generate', label: 'Generate Agent', color: 'gold',
    headline: 'Generate the fixes · human gate',
    desc: 'Generates the fix at the code, config, and dependency level, including CI/CD pipeline remediations, and validates it before anything ships: the original exploit is re-run and regression, integration, and security tests must pass. Each fix is proposed as a pull request with an AI explainability report. Nothing proceeds without your sign-off.',
    pills: ['Patch generation', 'Config changes', 'Dependency updates', 'Pre-ship validation', 'AI explainability', 'Human gate'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
  {
    id: 'apply', label: 'Apply Agent', color: 'blue',
    headline: 'Merge PRs · human gate',
    desc: 'Merges approved pull requests and applies config changes across repos, cloud, and infrastructure. Nothing merges without explicit approval, and every change is logged with who approved it and why.',
    pills: ['PR merge', 'Config rollout', 'Secret rotation', 'CI/CD integration', 'Human gate'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: 'deploy', label: 'Deploy Agent', color: 'blue',
    headline: 'Deploy to prod · human gate',
    desc: 'Ships the merged fix to production through your existing CI/CD pipeline. Staged rollout with health checks at every step, and a full audit trail for SOC 2 and compliance evidence. Every deploy is human-gated.',
    pills: ['Staged rollout', 'CI/CD pipeline', 'Health checks', 'Audit trail', 'Human gate'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
      </svg>
    ),
  },
  {
    id: 'rollback', label: 'Rollback Agent', color: 'emerald',
    headline: 'Verify & rollback · human gate',
    desc: 'Verifies the fix in production and monitors post-deploy: latency spikes, error rate increases, failed health checks. Triggers instant rollback on any anomaly with sub-60-second recovery. Confirmed findings become permanent regression tests.',
    pills: ['Verify in prod', 'Anomaly detection', 'Error rate watch', '<60s rollback', 'Human gate'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
];

// Competitive table from deck
const competitors = [
  { name: 'CrowdStrike / Palo Alto', scan: true, prioritize: true, fix: '~', verify: false, rollback: false, safe: false },
  { name: 'Wiz / Snyk', scan: true, prioritize: true, fix: '~', verify: false, rollback: false, safe: false },
  { name: 'Tenable / Qualys', scan: true, prioritize: true, fix: '~', verify: false, rollback: false, safe: false },
  { name: 'Aikido / Tenzai', scan: true, prioritize: true, fix: '~', verify: false, rollback: false, safe: false },
  { name: 'Torq / Seemplicity', scan: false, prioritize: true, fix: '~', verify: false, rollback: false, safe: false },
  { name: 'Dropzone AI / 7AI', scan: true, prioritize: true, fix: '~', verify: '~', rollback: false, safe: false },
  { name: 'Cyber Army AutoFix', scan: true, prioritize: true, fix: true, verify: true, rollback: true, safe: true, highlight: true },
];

const capabilities = [
  { icon: '⬡', title: 'CVE Auto-Patching', desc: 'OS packages, libraries, and containers patched by exploitability score, not just CVSS.', pills: ['CVE', 'OS patches', 'Libraries', 'Containers'], accent: 'emerald' },
  { icon: '⬡', title: 'Supply Chain Defense', desc: 'Continuously monitors dependencies, detects compromised packages (like Axios March 2026 or Chalk/Debug Sept 2025), and auto-patches across all repos before malicious code ships.', pills: ['npm/PyPI', 'Dep scanning', 'Auto-patch', 'OSV/CVE feed'], accent: 'blue' },
  { icon: '⬡', title: 'CI/CD Pipeline Fixes', desc: 'Code-level vulnerability remediation in GitHub and GitLab pipelines before changes reach production.', pills: ['GitHub', 'GitLab', 'Pipeline', 'Code-level'], accent: 'blue' },
  { icon: '⬡', title: 'Generate Agent: GitHub PR', desc: 'Scans repos for vulnerabilities and secrets, generates a targeted fix, and opens a pull request for human review. Nothing merges without approval.', pills: ['Repo scan', 'Auto fix', 'Auto PR', 'Secret detection', 'Human gate'], accent: 'gold' },
  { icon: '⬡', title: 'Cloud Misconfiguration', desc: 'AWS, GCP, Azure, and Kubernetes misconfigurations detected and corrected automatically.', pills: ['AWS', 'GCP', 'Azure', 'Kubernetes', 'IaC'], accent: 'emerald' },
  { icon: '⬡', title: 'Secret Rotation', desc: 'Hardcoded API keys, tokens, and credentials detected, rotated, and removed automatically.', pills: ['API keys', 'Tokens', 'Credentials'], accent: 'emerald' },
  { icon: '⬡', title: 'IAM & Access Policy', desc: 'Overprivileged roles and excessive permissions tightened automatically. Least-privilege enforced.', pills: ['IAM', 'RBAC', 'Least-priv'], accent: 'blue' },
  { icon: '⬡', title: 'Compliance Evidence', desc: 'SOC 2, PCI-DSS, HIPAA, and CIS gaps remediated with audit-ready evidence generated automatically.', pills: ['SOC 2', 'PCI-DSS', 'HIPAA', 'CIS'], accent: 'emerald' },
  { icon: '⬡', title: 'Surface-Level Monitoring', desc: 'Continuous external attack surface monitoring across TLS, DNS, exposed services, shadow IT, and certificate drift.', pills: ['TLS/SSL', 'DNS', 'Ports', 'Shadow IT'], accent: 'blue' },
  { icon: '⬡', title: 'AI Explainability', desc: 'Every fix includes an AI explainability report covering root cause, fix rationale, risk reduction, and compliance impact.', pills: ['Root cause', 'Fix rationale', 'Risk delta', 'Compliance'], accent: 'emerald' },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.06]">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 py-5 text-left group">
        <span className="text-sm font-medium text-white/80 group-hover:text-white/90 transition-colors">{q}</span>
        <span className={`flex-shrink-0 w-5 h-5 rounded-full border border-white/[0.12] flex items-center justify-center transition-transform duration-200 ${open ? 'rotate-45' : ''}`}>
          <svg className="w-2.5 h-2.5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
            <p className="pb-5 text-sm text-zinc-400 leading-relaxed max-w-2xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AutoFixClient() {
  return (
    <div className="bg-surface-base min-h-screen">

      {/* ── HERO ── */}
      <section className="relative overflow-hidden pt-8 pb-24 sm:pb-32 px-6">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(0,201,160,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,201,160,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
          <div className="absolute -top-48 -left-48 w-[700px] h-[700px] bg-accent/[0.05] rounded-full blur-[140px]" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-blue-600/[0.04] rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="mb-10">
            <div className="inline-flex items-center gap-2 text-xs text-zinc-500">
              <Link href="/" className="hover:text-zinc-300 transition-colors">Cyber Army</Link>
              <span>/</span>
              <span className="text-zinc-300">AutoFix</span>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            {/* Left */}
            <div>
              <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium tracking-wide uppercase mb-7">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  Agentic AI Security · Early Access
                </span>
              </motion.div>

              <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="visible"
                className="text-5xl sm:text-6xl lg:text-[64px] font-bold leading-[1.0] tracking-tight mb-6">
                <span className="text-white/90">Cyber</span><span className="text-accent">Army</span>
                <br />
                <span className="text-white/90">Auto</span><span className="text-accent">Fix</span>
              </motion.h1>

              {/* Loop strip */}
              <motion.div custom={2} variants={fadeUp} initial="hidden" animate="visible"
                className="flex items-center gap-1.5 flex-wrap mb-6">
                {['Discover', 'Prioritize', 'Generate', 'Apply', 'Deploy', 'Rollback'].map((s, i, arr) => (
                  <div key={s} className="flex items-center gap-1.5">
                    <span className="text-xs font-mono font-medium text-accent">{s}</span>
                    {i < arr.length - 1 && <span className="text-zinc-600 text-xs">→</span>}
                  </div>
                ))}
              </motion.div>

              <motion.p custom={3} variants={fadeUp} initial="hidden" animate="visible"
                className="text-lg text-zinc-400 leading-relaxed max-w-lg mb-4">
                The first agentic AI platform that <span className="text-white/80 font-medium">auto-remediates</span> security vulnerabilities on top of the tools you already run. AutoFix owns the last mile: fixes merged, deployed, and verified in production, including CI/CD pipeline and code-level fixes. No rip and replace.
              </motion.p>

              <motion.p custom={4} variants={fadeUp} initial="hidden" animate="visible"
                className="text-sm text-accent/80 font-mono mb-10">
                AI that doesn't just warn you. It fixes it.
              </motion.p>

              <motion.div custom={5} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-wrap gap-3">
                <Link href="/contact?lead_type=autofix_early_access&source=autofix_hero"
                  className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-accent text-black font-semibold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] text-sm">
                  Request Early Access
                  <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </Link>
                <Link href="#how-it-works" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-all duration-300 text-sm">
                  See How It Works
                </Link>
              </motion.div>
            </div>

            {/* Right - live terminal */}
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}>
              <div className="rounded-2xl border border-white/[0.07] bg-surface-1 overflow-hidden shadow-2xl shadow-black/60">
                <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/[0.06] bg-surface-deep">
                  <div className="w-3 h-3 rounded-full bg-red-500/60" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                  <div className="w-3 h-3 rounded-full bg-accent/60" />
                  <span className="ml-3 text-[10px] text-zinc-500 font-mono">autofix-agent - prod remediation loop</span>
                </div>
                <div className="p-5 font-mono text-xs space-y-2">
                  {[
                    { sym: '●', c: 'text-accent', t: '[discover] CVE-2024-3094 in openssh 9.6p1', dim: false },
                    { sym: '●', c: 'text-accent', t: '[discover] secrets leak in .github/workflows/deploy.yml', dim: false },
                    { sym: '→', c: 'text-zinc-600', t: '[prioritize] CVSS 10.0 + EPSS 0.94 - critical priority', dim: true },
                    { sym: '⚙', c: 'text-accent-secondary', t: '[generate] fix drafted: openssh 9.6p1 → 9.7p1 + secret rotation', dim: false },
                    { sym: '⬡', c: 'text-zinc-600', t: '[generate] validation - 847 tests passed · 0 regressions', dim: true },
                    { sym: '⚠', c: 'text-yellow-400', t: '[gate] CISO approval requested - awaiting sign-off', dim: false },
                    { sym: '✓', c: 'text-accent', t: '[gate] approved by john@acme.com at 14:23:07', dim: false },
                    { sym: '⚙', c: 'text-accent-secondary', t: '[apply] PR #412 merged - CI/CD pipeline ref updated', dim: false },
                    { sym: '⬆', c: 'text-accent-secondary', t: '[deploy] deploying to production...', dim: false },
                    { sym: '✓', c: 'text-accent', t: '[rollback] armed · verifying in prod - healthy', dim: false },
                    { sym: '✓', c: 'text-accent', t: '[done] 2 vulnerabilities fixed · prod healthy · logged', dim: false },
                  ].map((l, i) => (
                    <div key={i} className={`flex items-start gap-3 ${l.dim ? 'opacity-35' : ''}`}>
                      <span className={`${l.c} flex-shrink-0 w-3 text-center mt-0.5`}>{l.sym}</span>
                      <span className="text-zinc-300">{l.t}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-2 border-t border-white/[0.05] grid grid-cols-3 gap-3 text-center">
                    {[['4m 37s', 'total time'], ['0', 'prod breaks'], ['2', 'fixes shipped']].map(([val, label]) => (
                      <div key={label}>
                        <div className="text-accent font-bold">{val}</div>
                        <div className="text-zinc-600 text-[9px] mt-0.5">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stats below terminal */}
              <div className="grid grid-cols-3 gap-3 mt-3">
                {[
                  ['45+ days', 'traditional MTTF', 'text-zinc-500'],
                  ['→', '', 'text-accent'],
                  ['<1 hour', 'with AutoFix', 'text-accent'],
                ].map(([val, label, color], i) => (
                  <div key={i} className={`text-center rounded-xl border border-white/[0.06] bg-surface-1 py-4 px-2 ${i === 1 ? 'flex items-center justify-center' : ''}`}>
                    <div className={`text-lg font-bold ${color}`}>{val}</div>
                    {label && <div className="text-[9px] text-zinc-600 mt-0.5 uppercase tracking-wide">{label}</div>}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="relative overflow-hidden bg-surface-deep border-y border-accent/10 py-3">
        <style>{`
          @keyframes ticker {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .ticker-track {
            display: flex;
            width: max-content;
            animation: ticker 32s linear infinite;
          }
          .ticker-track:hover { animation-play-state: paused; }
          @media (prefers-reduced-motion: reduce) {
            .ticker-track { animation: none; }
          }
        `}</style>
        <div className="ticker-track">
          {[
            'CVE-2024-1234 PATCHED',
            'MISCONFIGURATION REMEDIATED',
            'SECRET ROTATED',
            'COMPLIANCE GAP CLOSED',
            'RISKY POLICY REVOKED',
            'FIX VERIFIED IN PROD',
            'ROLLBACK READY',
            'CVE-2024-5678 PATCHED',
            'ACCESS POLICY HARDENED',
            'FIX DEPLOYED · PROD SAFE',
            'IAM OVER-PRIVILEGE REMOVED',
            'REGRESSION TESTS PASSED',
            // duplicate for seamless loop
            'CVE-2024-1234 PATCHED',
            'MISCONFIGURATION REMEDIATED',
            'SECRET ROTATED',
            'COMPLIANCE GAP CLOSED',
            'RISKY POLICY REVOKED',
            'FIX VERIFIED IN PROD',
            'ROLLBACK READY',
            'CVE-2024-5678 PATCHED',
            'ACCESS POLICY HARDENED',
            'FIX DEPLOYED · PROD SAFE',
            'IAM OVER-PRIVILEGE REMOVED',
            'REGRESSION TESTS PASSED',
          ].map((item, i) => (
            <span key={i} className="inline-flex items-center gap-3 px-6 text-xs font-mono font-medium tracking-widest text-accent/70 whitespace-nowrap">
              <span className="w-1 h-1 rounded-full bg-accent/40 flex-shrink-0" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── THE PROBLEM ── */}
      <InView>
        <section className="px-6 py-24 sm:py-32 bg-surface-1 border-y border-white/[0.06]">
          <div className="max-w-6xl mx-auto">
            <SectionLabel>The Problem</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">Security teams are drowning.</h2>
            <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-14 italic">The CISO dichotomy: move fast and risk production or move slow and stay vulnerable.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ['Attack Surface Explosion', 'Hundreds of SaaS apps, APIs, AI agents, and vendors per enterprise. Security teams can\'t see what they don\'t control.'],
                ['Alert Fatigue Without Action', 'SOCs receive ~960 alerts/day. 40% are never investigated. Analysts spend 25-30 min per false positive.'],
                ['Supply-Chain Blindness', 'Vulnerabilities propagate in real-time across dependencies. Traditional scanners miss live-exploited CVEs.'],
                ['Slow Remediation = Risk', 'Median time to remediate critical vulns is 55-65 days. No sandbox. No rollback guarantee.'],
              ].map(([title, desc]) => (
                <div key={title} className="rounded-2xl border border-white/[0.06] bg-surface-base p-7">
                  <div className="w-0.5 h-6 bg-red-500/40 rounded-full mb-4" />
                  <h3 className="text-sm font-semibold text-white/90 mb-2">{title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-zinc-700 mt-6">Sources: Dropzone AI 2025, Cymulate 2025, Verizon 2024 DBIR, Edgescan 2022, BetterCloud 2024</p>
          </div>
        </section>
      </InView>

      {/* ── SIX AGENTS ── */}
      <section id="how-it-works" className="px-6 py-24 sm:py-32 max-w-6xl mx-auto">
        <InView>
          <SectionLabel>Architecture</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">Six specialized agents. One loop.</h2>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-4">Each agent owns one stage of the remediation pipeline. Together they form a closed loop: every fix is validated before it ships, deployed through your CI/CD, and verified in production, with sub-60-second rollback armed.</p>
          <p className="text-sm text-accent/70 font-mono mb-14">Human gate on Generate, Apply, Deploy, and Rollback. Every auto-fix is human-gated today. Autonomy expands as trust grows.</p>
        </InView>

        {/* Loop flow strip */}
        <InView>
          <div className="flex items-center justify-between mb-10 overflow-x-auto pb-2">
            {['Discover', 'Prioritize', 'Generate', 'Apply', 'Deploy', 'Rollback'].map((s, i, arr) => {
              const gated = i >= 2;
              return (
                <div key={s} className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex flex-col items-center gap-1">
                    <div className={`w-10 h-10 rounded-full border flex items-center justify-center text-xs font-bold ${gated ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400' : 'border-accent/20 bg-accent/8 text-accent'}`}>
                      {i + 1}
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500">{s}</span>
                    <span className={`text-[8px] font-mono uppercase tracking-wide ${gated ? 'text-yellow-500/70' : 'text-transparent'}`}>{gated ? 'human gate' : '·'}</span>
                  </div>
                  {i < arr.length - 1 && <span className="text-zinc-700 mx-2 flex-shrink-0">→</span>}
                </div>
              );
            })}
          </div>
        </InView>

        <div className="space-y-px rounded-2xl overflow-hidden border border-white/[0.06]">
          {agents.map((agent, i) => (
            <ScrollFadeIn key={agent.id} delay={i * 60}>
              <div className="group flex gap-6 p-8 bg-surface-1 hover:bg-surface-2 transition-all duration-200 border-l-2 border-transparent"
                onMouseEnter={e => (e.currentTarget.style.borderLeftColor = agent.color === 'blue' ? '#3b82f6' : agent.color === 'gold' ? '#F0C040' : '#10b981')}
                onMouseLeave={e => (e.currentTarget.style.borderLeftColor = 'transparent')}>
                <div className="flex-shrink-0 flex flex-col items-center gap-2 w-12">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${agent.color === 'blue' ? 'bg-accent-secondary/10 border border-accent-secondary/20 text-accent-secondary' : agent.color === 'gold' ? 'bg-[#F0C040]/10 border border-[#F0C040]/20 text-[#F0C040]' : 'bg-accent/10 border border-accent/20 text-accent'}`}>
                    {agent.icon}
                  </div>
                  <span className="text-[9px] font-mono text-zinc-700">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                    <h3 className="text-sm font-semibold text-white/90">{agent.label}</h3>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${agent.color === 'blue' ? 'bg-accent-secondary/8 border-accent-secondary/15 text-accent-secondary' : agent.color === 'gold' ? 'bg-[#F0C040]/8 border-[#F0C040]/15 text-[#F0C040]' : 'bg-accent/8 border-accent/15 text-accent'}`}>{agent.headline}</span>
                  </div>
                  <p className="text-sm text-zinc-500 leading-relaxed mb-4 max-w-2xl">{agent.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {agent.pills.map(p => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.06] text-zinc-500 font-mono">{p}</span>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </section>

      {/* ── CISO CONTROL CENTER ── */}
      <InView>
        <section className="px-6 py-24 sm:py-32 bg-surface-1 border-y border-white/[0.06]">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionLabel>CISO Control Center</SectionLabel>
              <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-6">
                CISO peace of mind.<br /><span className="text-accent">Full control. Zero toil.</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-10 text-lg">
                AutoFix is built around the CISO. Every proposed fix surfaces with an AI explainability report. You approve with a single click. Nothing deploys without your sign-off. Full audit trails satisfy every compliance requirement.
              </p>
              <ul className="space-y-4">
                {[
                  ['Human approval gate', 'Every fix requires explicit CISO or security team sign-off before execution'],
                  ['AI explainability report', 'Root cause, fix rationale, risk reduction, and blast radius per fix'],
                  ['CISO dashboard', 'Real-time view across all assets, fixes in flight, and verified outcomes'],
                  ['Full audit trail', 'Every action logged with timestamps, approvals, and evidence for SOC 2 / HIPAA'],
                  ['Policy-as-code guardrails', 'Define what AutoFix can and cannot touch, enforced at the agent level'],
                  ['Unified response', 'One platform for monitoring, remediation, verification, and rollback'],
                ].map(([title, desc]) => (
                  <li key={title} className="flex items-start gap-3 text-sm">
                    <Check />
                    <span className="text-zinc-300"><span className="font-medium text-white/90">{title}</span> - <span className="text-zinc-400">{desc}</span></span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CISO dashboard mockup */}
            <div className="rounded-2xl border border-white/[0.07] bg-surface-base overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] bg-surface-deep">
                <span className="text-[10px] font-mono text-zinc-400">CISO Control Center</span>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  <span className="text-[10px] text-zinc-500">Live</span>
                </div>
              </div>
              <div className="p-5 space-y-4">
                {/* Stats row */}
                <div className="grid grid-cols-3 gap-2">
                  {[['14,892', 'Vulns Patched', 'text-accent'], ['0', 'Prod Breaks', 'text-accent'], ['99.997%', 'Uptime', 'text-accent-secondary']].map(([val, label, c]) => (
                    <div key={label} className="rounded-lg border border-white/[0.05] bg-surface-1 p-3 text-center">
                      <div className={`text-base font-bold ${c}`}>{val}</div>
                      <div className="text-[9px] text-zinc-600 mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
                {/* Approval queue */}
                <div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-2 font-mono">Pending approval</p>
                  {[
                    { sev: 'CRIT', title: 'openssh upgrade 9.6p1 → 9.7p1', asset: 'prod-api-01', status: 'awaiting' },
                    { sev: 'HIGH', title: 'S3 bucket public access blocked', asset: 'data-lake-prod', status: 'awaiting' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-center gap-3 py-2.5 border-b border-white/[0.04] text-xs">
                      <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 flex-shrink-0">{item.sev}</span>
                      <span className="text-zinc-300 flex-1 truncate">{item.title}</span>
                      <span className="text-zinc-700 font-mono text-[9px] hidden sm:block">{item.asset}</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full border border-yellow-500/20 bg-yellow-500/8 text-yellow-400 flex-shrink-0">Review →</span>
                    </div>
                  ))}
                </div>
                {/* Recent */}
                <div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-2 font-mono">Recently approved & deployed</p>
                  {[
                    { title: 'log4j CVE patched across 47 services', time: '12m ago' },
                    { title: 'IAM over-privilege corrected - 3 roles', time: '1h ago' },
                    { title: 'Secret rotated - GitHub Actions ref updated', time: '2h ago' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-center gap-3 py-2 text-xs">
                      <svg className="w-3 h-3 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                      <span className="text-zinc-400 flex-1">{item.title}</span>
                      <span className="text-zinc-700 text-[9px]">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </InView>

      {/* ── CAPABILITIES ── */}
      <section className="px-6 py-24 sm:py-32 max-w-6xl mx-auto">
        <InView>
          <SectionLabel>What AutoFix Covers</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">The full surface. Every layer.</h2>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-6">AutoFix unifies findings across EASM, DAST, SAST, and CSPM, alongside its own scanners and the tools you already run, then remediates every category of risk across your stack, from CVEs in OS packages to secrets in CI/CD pipelines.</p>
          <div className="flex flex-wrap gap-2 mb-14">
            {['EASM', 'DAST', 'SAST', 'CSPM', 'SCA', 'IaC', 'Secrets', 'CVEs'].map((c) => (
              <span key={c} className="text-[11px] font-mono px-2.5 py-1 rounded-md border border-accent/15 bg-accent/[0.05] text-accent">{c}</span>
            ))}
          </div>
        </InView>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {capabilities.map((cap, i) => (
            <ScrollFadeIn key={cap.title} delay={i * 50}>
              <div className={`h-full rounded-2xl border bg-surface-1 hover:bg-surface-2 transition-all duration-300 p-6 ${cap.accent === 'blue' ? 'border-white/[0.06] hover:border-accent-secondary/15' : 'border-white/[0.06] hover:border-accent/15'}`}>
                <h3 className="text-sm font-semibold text-white/90 mb-2">{cap.title}</h3>
                <p className="text-xs text-zinc-500 leading-relaxed mb-4">{cap.desc}</p>
                <div className="flex flex-wrap gap-1">
                  {cap.pills.map(p => (
                    <span key={p} className={`text-[9px] px-1.5 py-0.5 rounded border font-mono ${cap.accent === 'blue' ? 'bg-accent-secondary/[0.05] border-accent-secondary/15 text-accent-secondary' : 'bg-accent/[0.05] border-accent/15 text-accent'}`}>{p}</span>
                  ))}
                </div>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </section>

      {/* ── COMPETITIVE LANDSCAPE ── */}
      <InView>
        <section className="px-6 py-24 sm:py-32 bg-surface-1 border-y border-white/[0.06]">
          <div className="max-w-6xl mx-auto">
            <SectionLabel>Competitive Landscape</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">Everyone generates fixes. Who gets them merged?</h2>
            <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-12">Scanners stop at the list. SOAR platforms stop at the playbook. AutoFix owns the last mile: the fix gets merged, deployed, and verified in production, with automated rollback armed.</p>

            <div className="rounded-2xl border border-white/[0.07] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06] bg-surface-base">
                      <th className="text-left px-5 py-4 text-zinc-500 font-medium text-xs" />
                      {['Scan', 'Prioritize', 'Generate PR', 'Verify in CI/CD', 'Monitor / Rollback', 'Prod-Safe'].map(h => (
                        <th key={h} className="px-4 py-4 text-center text-xs font-medium text-zinc-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.map((c, i) => (
                      <tr key={c.name} className={`border-b border-white/[0.04] ${c.highlight ? 'bg-accent/[0.04]' : i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
                        <td className={`px-5 py-4 text-sm font-medium ${c.highlight ? 'text-accent' : 'text-zinc-400'}`}>{c.name}</td>
                        {[c.scan, c.prioritize, c.fix, c.verify, c.rollback, c.safe].map((val, j) => (
                          <td key={j} className="px-4 py-4 text-center">
                            {val === true
                              ? <svg className={`w-4 h-4 mx-auto ${c.highlight ? 'text-accent' : 'text-zinc-400'}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                              : val === false
                              ? <span className="text-zinc-700 text-base">-</span>
                              : <span className="text-yellow-600 text-sm font-mono">~</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-[10px] text-zinc-700 mt-4">~ = partial / roadmap item. Sources: public product documentation.</p>
          </div>
        </section>
      </InView>

      {/* ── SUPPLY CHAIN SECURITY ── */}
      <InView>
        <section className="px-6 py-24 sm:py-32 bg-surface-1 border-y border-white/[0.06]">
          <div className="max-w-6xl mx-auto">
            <SectionLabel>Supply Chain Security</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">
              The attack is in your dependencies.
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-4">
              Modern attacks no longer target your perimeter. They target the packages, libraries, and open source components your code depends on, compromising them upstream before they reach you.
            </p>
            <p className="text-sm text-accent/80 font-mono mb-14">
              AutoFix monitors your entire dependency tree and auto-patches compromised packages before they ship to production.
            </p>

            {/* Recent incidents */}
            <div className="grid sm:grid-cols-3 gap-4 mb-14">
              {[
                {
                  date: 'March 2026',
                  name: 'Axios npm Attack',
                  severity: 'CRITICAL',
                  desc: 'Malicious versions 1.14.1 and 0.30.4 of Axios (100M+ weekly downloads) distributed a cross-platform remote access trojan. Available for roughly three hours before removal.',
                  impact: '100M+ weekly downloads exposed',
                },
                {
                  date: 'September 2025',
                  name: 'Shai-Hulud Worm',
                  severity: 'CRITICAL',
                  desc: 'Self-propagating worm spread across 500+ npm packages, stealing cloud secrets and automatically republishing infected versions to extend its reach.',
                  impact: '500+ packages compromised',
                },
                {
                  date: 'September 2025',
                  name: 'Chalk / Debug Compromise',
                  severity: 'HIGH',
                  desc: '18 popular packages including chalk and debug hijacked via maintainer phishing. Crypto-stealing malware embedded in packages with over 2 billion weekly downloads.',
                  impact: '2B+ weekly downloads affected',
                },
              ].map((incident) => (
                <div key={incident.name} className="rounded-2xl border border-white/[0.06] bg-surface-base p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-mono text-zinc-600">{incident.date}</span>
                    <span className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded border ${incident.severity === 'CRITICAL' ? 'text-red-400 border-red-500/20 bg-red-500/8' : 'text-orange-400 border-orange-500/20 bg-orange-500/8'}`}>
                      {incident.severity}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-white/90 mb-2">{incident.name}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed mb-4">{incident.desc}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
                    <span className="text-[10px] text-red-400/80 font-mono">{incident.impact}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* How AutoFix defends */}
            <div className="rounded-2xl border border-accent/15 bg-surface-base p-8">
              <p className="text-xs font-medium text-accent uppercase tracking-widest mb-6">How AutoFix responds</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  ['Continuous dependency scanning', 'Monitors package manifests and lockfiles across all repos in real time against npm advisories, OSV, and CVE feeds.'],
                  ['Compromise detection', 'Detects version anomalies, unexpected republications, and behavioral signatures of malicious packages.'],
                  ['Auto-patch in minutes', 'Generates a pinned upgrade to the safe version, validates it, and deploys before your team has finished triaging.'],
                  ['Full audit trail', 'Every detection, patch, and deployment logged with timestamps for SOC 2 and compliance evidence.'],
                ].map(([title, desc]) => (
                  <div key={title}>
                    <div className="w-0.5 h-5 bg-accent/40 rounded-full mb-3" />
                    <h4 className="text-sm font-semibold text-white/90 mb-1.5">{title}</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </InView>

      {/* ── WHY NOW ── */}
      <InView>
        <section className="px-6 py-24 sm:py-32 max-w-6xl mx-auto">
          <SectionLabel>Why Now</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-12">The timing is right.</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              ['Legacy Tools Hit a Wall', 'Point solutions generate alerts but can\'t remediate. CISOs are consolidating vendors and demanding outcomes, not dashboards. 75% of orgs pursuing vendor consolidation (Gartner 2022).'],
              ['Attack Surface Acceleration', 'SaaS proliferation, AI agent adoption, and supply-chain dependencies have expanded the enterprise attack surface beyond what manual teams can secure.'],
              ['LLMs Enable Agentic Security', 'For the first time, LLMs can reason about code, infrastructure, and configurations, then use tools to act on that reasoning. This unlocks autonomous remediation. Our agents are built on Anthropic Claude today; long term, we plan to train our own cybersecurity-specific LLMs.'],
            ].map(([title, desc]) => (
              <div key={title} className="rounded-2xl border border-white/[0.06] bg-surface-1 p-7">
                <div className="w-0.5 h-6 bg-accent/40 rounded-full mb-5" />
                <h3 className="text-sm font-semibold text-white/90 mb-3">{title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>
      </InView>

      {/* ── FAQ ── */}
      <InView>
        <section className="px-6 py-24 sm:py-32 bg-surface-1 border-y border-white/[0.06]">
          <div className="max-w-3xl mx-auto">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="text-2xl sm:text-3xl font-bold text-white/90 tracking-tight mb-10">Common questions.</h2>
            <div className="divide-y divide-white/[0.06]">
              {[
                { q: 'What is CyberArmy AutoFix?', a: 'CyberArmy AutoFix is an agentic AI security platform that autonomously discovers vulnerabilities, prioritizes by real exploitability, generates and applies fixes at the CI/CD pipeline and code level, validates every fix before it ships, and deploys with instant rollback. The first platform to never break production.' },
                { q: 'How does AutoFix work as a CISO control center?', a: 'Every auto-fix is human-gated: the Generate, Apply, Deploy, and Rollback stages each carry an explicit approval gate. CISOs see every proposed fix with an AI explainability report covering root cause, fix rationale, and risk reduction, then approve with a single click. No fix merges or deploys without explicit sign-off. Full audit trails satisfy SOC 2, HIPAA, and PCI-DSS requirements.' },
                { q: 'Can AutoFix fix vulnerabilities at the CI/CD pipeline and code level?', a: 'Yes. AutoFix integrates with GitHub, GitLab, and CI/CD pipelines to detect and remediate code-level vulnerabilities, hardcoded secrets, dependency issues, and misconfigured pipeline steps, automatically, before they reach production.' },
                { q: 'How is AutoFix different from CrowdStrike, Wiz, Tenable, or Qualys?', a: 'Those platforms scan, prioritize, and at best generate a fix, then hand it back to your team. AutoFix closes the entire loop: discover, prioritize, generate, apply, deploy, and rollback, with every fix validated before it ships. Nobody else gets the fix merged, deployed, and verified in production. Never Breaks Prod™ is unclaimed territory.' },
                { q: 'What is agentic AI security?', a: 'Agentic AI security uses specialized AI agents that reason about code, infrastructure, and configurations, then take autonomous action to remediate vulnerabilities. Unlike traditional automation, agentic AI understands context and blast radius before executing any fix.' },
                { q: 'Does AutoFix work with Cyber Swarm?', a: 'Yes, they close the loop together. Cyber Swarm finds and verifies exploitable issues from the outside. AutoFix remediates at the code and infrastructure level, validates the fix before deploy, verifies it in production, and arms rollback. Confirmed findings become permanent regression tests, so a fixed issue cannot quietly come back.' },
                { q: 'Is AutoFix available now?', a: 'AutoFix is in early access. We are collaborating with CISO design partners to refine the product. Request early access to join the waitlist and be among the first organizations to deploy autonomous remediation.' },
              ].map(faq => <FaqItem key={faq.q} {...faq} />)}
            </div>
          </div>
        </section>
      </InView>

      {/* ── EARLY ACCESS CTA ── */}
      <section className="px-6 py-24 sm:py-32 max-w-5xl mx-auto">
        <InView>
          <div className="relative rounded-2xl border border-accent/15 overflow-hidden p-12 sm:p-20 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#031a14]/30 via-surface-deepest to-[#0f1240]/10 pointer-events-none" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16 bg-gradient-to-b from-emerald-500/40 to-transparent" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Early Access Program
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-5">
                Be first to deploy<br />autonomous remediation.
              </h2>
              <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-4">
                We are selecting CISO design partners to shape the AutoFix platform. Early access includes direct engineering collaboration, custom integration support, and priority onboarding.
              </p>
              <p className="text-sm text-zinc-500 max-w-xl mx-auto mb-10">
                Currently active: 5 CISO collaborations · 2 red team engagements · Crawler + agent prototypes deployed
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link href="/contact?lead_type=autofix_early_access&source=autofix_cta"
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-accent text-black font-bold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 hover:shadow-accent/35 hover:scale-[1.02] active:scale-[0.98]">
                  Request Early Access
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </Link>
                <Link href="/cyberswarm"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-all duration-300 hover:bg-white/[0.03]">
                  Try Cyber Swarm →
                </Link>
              </div>
              <p className="text-xs text-zinc-700 mt-6">No commitment required · Limited spots available · CISO-level engagement</p>
            </div>
          </div>
        </InView>
      </section>
    </div>
  );
}
